import { CalendarIcon } from '@heroicons/react/20/solid';
import { format } from 'date-fns';
import { useState } from 'react';
import type { ReactNode } from 'react';
import moment from 'moment';

type DateType = string | number | Date;
export type DateAccordionProps = {
  /** The title of the Accordion */
  title: string;
  /** Date object which contains startDate, & endDate both of which are strings.
   * If you need to convert your Date type to string use the util function `formatDate`.
   */
  date: {
    startDate: string;
    endDate: string;
  };
  /**
   * The Body of The Accordion
   * What you will see when you click on it
   */
  children: ReactNode;
  /** Optional Status when it's passed the border left change its color from gray to green / primary color depending on whether it's completed or not */
  isCompleted?: boolean;
  /** For adding classes to the container */
  className?: string;
};

function DateAccordion({
  isCompleted,
  title,
  date,
  children,
  className,
}: DateAccordionProps) {
  const [showContent, setShowContent] = useState(false);
  const { startDate, endDate } = date;
  return (
    <div className={className}>
      <div
        onClick={() => setShowContent(!showContent)}
        className={`cursor-pointer hover:bg-gray-50 border-l-4 ${
          isCompleted !== undefined
            ? isCompleted
              ? 'border-green-500'
              : 'border-primary-500'
            : ''
        }`}
        title={
          isCompleted !== undefined
            ? `${isCompleted ? 'Completed' : 'Not completed'}`
            : undefined
        }
      >
        <div className="px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-primary-600 truncate">
              {title}
            </p>
          </div>
          <div className="mt-2 sm:flex sm:justify-between">
            <div className="sm:flex">
              <p className="flex items-center text-xs text-gray-500">
                <CalendarIcon
                  className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
                Starts {format(new Date(startDate), 'MM/dd/yyyy HH:mm')}
              </p>
              <p className="mt-2 flex items-center text-xs text-gray-500 sm:mt-0 sm:ml-6">
                <CalendarIcon
                  className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
                Ends {format(new Date(endDate), 'MM/dd/yyyy HH:mm')}
              </p>
            </div>
          </div>
        </div>
      </div>
      {showContent && children}
    </div>
  );
}

export default DateAccordion;

export function formatDate(date: DateType) {
  return moment(date).format(moment.HTML5_FMT.DATETIME_LOCAL);
}
